<!--
@component

For internal links within the application only, adds global query params to 
links to preserve page state on reloads and across new tabs. 

```svelte
<Link href={href_without_params} queryParams={paramsObject} />
```
-->
<script lang="ts">
import type { HTMLAnchorAttributes } from 'svelte/elements';

import { clickOutside } from '@viamrobotics/prime-core';

import { withGlobalQueryParams } from './with-query-params.svelte';

interface Props extends HTMLAnchorAttributes {
  href: string;
  onClickOutside?: () => void;
  openInNewTab?: boolean;
}

const {
  href,
  children,
  onClickOutside,
  openInNewTab,
  target: targetProp,
  rel: relProp,
  ...attributes
}: Props = $props();

const url = $derived(withGlobalQueryParams(href));
const target = $derived(openInNewTab ? '_blank' : targetProp);
const rel = $derived(
  openInNewTab
    ? [relProp, 'noopener', 'noreferrer'].filter(Boolean).join(' ')
    : relProp
);
</script>

<a
  href={url.url}
  {target}
  {rel}
  {...attributes}
  use:clickOutside={onClickOutside}
>
  {@render children?.()}
</a>
